import { useState, useEffect, useMemo, useCallback } from "react";
import { stateManager } from "@/lib/state-manager";

/**
 * Custom hook for managing persistent state in localStorage
 * @param {Object} config - Configuration object
 * @param {string} config.endpoint - Unique endpoint identifier
 * @param {string} config.version - Version identifier for state schema
 * @param {Object} config.defaultState - Default state values
 * @param {boolean} config.autoSave - Whether to automatically save state changes (default: true)
 * @returns {Array} [state, setState, { clearState, updateState }]
 */
export function usePersistentState({
  endpoint,
  version = "1",
  defaultState = {},
  autoSave = true,
}) {
  const stateContext = useMemo(
    () => ({ endpoint, version }),
    [endpoint, version]
  );

  const [state, setStateInternal] = useState(() => {
    // Initialize state manager and get saved state
    stateManager.init(stateContext);
    const savedState = stateManager.getState(stateContext, defaultState);
    return savedState;
  });

  // Auto-save state changes to localStorage
  useEffect(() => {
    if (autoSave && state !== null && Object.keys(state).length > 0) {
      stateManager.setState(stateContext, state);
    }
  }, [state, stateContext, autoSave]);

  // Enhanced setState that can accept partial updates or functions
  const setState = useCallback((updater) => {
    setStateInternal((prevState) => {
      if (typeof updater === "function") {
        return { ...prevState, ...updater(prevState) };
      }
      return { ...prevState, ...updater };
    });
  }, []);

  // Clear all saved state
  const clearState = useCallback(() => {
    stateManager.clear(stateContext);
    setStateInternal(defaultState);
  }, [stateContext, defaultState]);

  // Update state without triggering re-render (useful for batch updates)
  const updateState = useCallback(
    (updater) => {
      stateManager.updateState(stateContext, updater);
    },
    [stateContext]
  );

  return [state, setState, { clearState, updateState }];
}
